import { useMemo, useEffect, useState } from "react";

interface HistogramProps {
  imageData?: ImageData;
  previewUrl?: string;
  className?: string;
  overlay?: boolean;
}

export function Histogram({
  imageData,
  previewUrl,
  className = "",
  overlay = false,
}: HistogramProps) {
  const [loadedImageData, setLoadedImageData] = useState<ImageData | null>(null);

  useEffect(() => {
    if (!previewUrl) {
      setLoadedImageData(null);
      return;
    }

    const loadImageData = async () => {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";

        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("Failed to load image"));
          img.src = previewUrl;
        });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setLoadedImageData(imageData);
      } catch (error) {
        console.error("Failed to load image for histogram:", error);
        setLoadedImageData(null);
      }
    };

    loadImageData();
  }, [previewUrl]);

  // Use provided imageData or loaded imageData
  const activeImageData = imageData || loadedImageData;
  const histogramData = useMemo(() => {
    if (!activeImageData) {
      return null;
    }

    // Initialize histogram arrays for R, G, B channels
    const red = new Array(256).fill(0);
    const green = new Array(256).fill(0);
    const blue = new Array(256).fill(0);
    const luminance = new Array(256).fill(0);

    // Process image data
    const data = activeImageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      red[r]++;
      green[g]++;
      blue[b]++;

      // Calculate luminance using standard formula
      const lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      luminance[lum]++;
    }

    // Apply square root scaling to compress spikes while preserving detail
    const sqrtRed = red.map((v) => Math.sqrt(v));
    const sqrtGreen = green.map((v) => Math.sqrt(v));
    const sqrtBlue = blue.map((v) => Math.sqrt(v));
    const sqrtLuminance = luminance.map((v) => Math.sqrt(v));

    // Find max values for normalization
    const maxRed = Math.max(...sqrtRed);
    const maxGreen = Math.max(...sqrtGreen);
    const maxBlue = Math.max(...sqrtBlue);
    const maxLuminance = Math.max(...sqrtLuminance);

    return {
      red: sqrtRed.map((v) => v / maxRed),
      green: sqrtGreen.map((v) => v / maxGreen),
      blue: sqrtBlue.map((v) => v / maxBlue),
      luminance: sqrtLuminance.map((v) => v / maxLuminance),
    };
  }, [activeImageData]);

  if (!histogramData) {
    if (overlay) {
      return null; // Don't show anything if no image data and it's an overlay
    }
    return (
      <div className={`border-border border-t bg-background p-4 ${className}`}>
        <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
          Load an image to see histogram
        </div>
      </div>
    );
  }

  const svgWidth = overlay ? 205 : 256; // 20% smaller for overlay: 256 * 0.8 = 205
  const svgHeight = overlay ? 96 : 100; // 20% smaller for overlay: 120 * 0.8 = 96

  const createPath = (values: number[], _color: string) => {
    let path = `M 0 ${svgHeight}`;

    for (let i = 0; i < values.length; i++) {
      const x = i;
      const y = svgHeight - values[i] * svgHeight;
      path += ` L ${x} ${y}`;
    }

    path += ` L ${svgWidth} ${svgHeight} Z`;
    return path;
  };

  const baseClasses = overlay
    ? "absolute bottom-4 left-4 rounded border border-border/50 bg-background/70 p-1 backdrop-blur-sm pointer-events-none"
    : "border-border border-t bg-background p-4";

  return (
    <div className={`${baseClasses} ${className}`}>
      {!overlay && <h3 className="font-medium text-foreground text-sm mb-2">Histogram</h3>}
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className={`rounded ${overlay ? "bg-muted/20" : "border bg-muted/20"}`}
        aria-label="Image histogram showing RGB and luminance distribution"
        role="img"
      >
        {/* Grid lines */}
        <defs>
          <pattern id="histogram-grid" width="64" height="30" patternUnits="userSpaceOnUse">
            <path
              d="M 64 0 L 0 0 0 30"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.5"
              opacity={overlay ? "0.1" : "0.2"}
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#histogram-grid)" />

        {/* RGB Channels - draw first (behind) */}
        <path
          d={createPath(histogramData.red, "red")}
          fill="rgba(239, 68, 68, 0.3)"
          stroke="rgba(220, 38, 38, 1)"
          strokeWidth="1.5"
        />

        <path
          d={createPath(histogramData.green, "green")}
          fill="rgba(34, 197, 94, 0.3)"
          stroke="rgba(22, 163, 74, 1)"
          strokeWidth="1.5"
        />

        <path
          d={createPath(histogramData.blue, "blue")}
          fill="rgba(59, 130, 246, 0.3)"
          stroke="rgba(37, 99, 235, 1)"
          strokeWidth="1.5"
        />

        {/* Luminance (grayscale) - draw last (in front) */}
        <path
          d={createPath(histogramData.luminance, "gray")}
          fill="none"
          stroke="rgba(160, 160, 160, 0.6)"
          strokeWidth="1.5"
        />
      </svg>

      {/* Legend - only show in non-overlay mode */}
      {!overlay && (
        <div className="flex justify-center gap-4 text-xs mt-2">
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            <span className="text-muted-foreground">Red</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            <span className="text-muted-foreground">Green</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-blue-500" />
            <span className="text-muted-foreground">Blue</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-gray-500" />
            <span className="text-muted-foreground">Luma</span>
          </div>
        </div>
      )}
    </div>
  );
}
